import { auth } from './firebase';

// ── Timeout constants ──────────────────────────────────────
export const SESSION_TIMEOUT_MS = 24 * 60 * 60 * 1000; // 24 hours
export const GRACE_PERIOD_MS = 2 * 60 * 60 * 1000;     // 2 hours after timeout
export const HARD_TIMEOUT_MS = SESSION_TIMEOUT_MS + GRACE_PERIOD_MS; // 26 hours
export const WARNING_THRESHOLD_MS = 23.5 * 60 * 60 * 1000; // 30 min before timeout

// ── Session states ─────────────────────────────────────────
export const SESSION_STATE = {
    ACTIVE: 'active',
    WARNING: 'warning',         // 30 min before timeout
    GRACE: 'grace',             // Past timeout, within grace period
    GRACE_URGENT: 'grace_urgent', // Last 30 min of grace period
    EXPIRED: 'expired',         // Past hard timeout
};

/**
 * Compute session state from sessionStartTime.
 * @param {string|Date|number} sessionStartTime
 * @returns {string} SESSION_STATE value
 */
export const getSessionState = (sessionStartTime) => {
    if (!sessionStartTime) return SESSION_STATE.ACTIVE;
    const elapsed = Date.now() - new Date(sessionStartTime).getTime();

    if (elapsed >= HARD_TIMEOUT_MS) return SESSION_STATE.EXPIRED;
    if (elapsed >= SESSION_TIMEOUT_MS + GRACE_PERIOD_MS - 30 * 60 * 1000) return SESSION_STATE.GRACE_URGENT;
    if (elapsed >= SESSION_TIMEOUT_MS) return SESSION_STATE.GRACE;
    if (elapsed >= WARNING_THRESHOLD_MS) return SESSION_STATE.WARNING;
    return SESSION_STATE.ACTIVE;
};

/**
 * Milliseconds remaining in the grace window (0 if not in grace or expired).
 */
export const getGraceTimeRemaining = (sessionStartTime) => {
    if (!sessionStartTime) return null;
    const elapsed = Date.now() - new Date(sessionStartTime).getTime();
    if (elapsed < SESSION_TIMEOUT_MS) return null; // not in grace yet
    const remaining = HARD_TIMEOUT_MS - elapsed;
    return Math.max(0, remaining);
};

/**
 * Milliseconds remaining until the session enters the grace period (null outside WARNING window).
 * Used by the sticky WARNING banner so users can see exactly how long they have to extend.
 */
export const getWarningTimeRemaining = (sessionStartTime) => {
    if (!sessionStartTime) return null;
    const elapsed = Date.now() - new Date(sessionStartTime).getTime();
    if (elapsed < WARNING_THRESHOLD_MS) return null; // not in warning yet
    if (elapsed >= SESSION_TIMEOUT_MS) return null;   // past warning, already in grace
    const remaining = SESSION_TIMEOUT_MS - elapsed;
    return Math.max(0, remaining);
};

/**
 * Extend session by writing a fresh sessionStartTime to Firestore.
 * Only meaningful when state is ACTIVE or WARNING.
 */
export const extendSession = async (userId) => {
    if (!userId) return false;
    try {
        const { updateDataFunction } = await import('./firestoreService');
        await updateDataFunction(`/users/${userId}`, {
            sessionStartTime: new Date().toISOString(),
        });
        return true;
    } catch (error) {
        console.error('[sessionTimeout] Failed to extend session:', error);
        return false;
    }
};

// ── Existing helpers (unchanged) ───────────────────────────

// Sign out and mark session as expired
export const signOutDueToTimeout = async () => {
    // Set a flag to show expired message on login page
    sessionStorage.setItem('sessionExpired', 'true');
    await auth.signOut();
};

// Check if user was signed out due to session expiry
export const wasSessionExpired = () => {
    return sessionStorage.getItem('sessionExpired') === 'true';
};

// Clear the session expired flag
export const clearSessionExpiredFlag = () => {
    sessionStorage.removeItem('sessionExpired');
};
